import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"
import { platformProviderEnvironmentV1Schema } from "./platform-provider-integration-v1.js"
import { platformQueryEnvelopeV1Schema } from "./tenant-directory-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)

export const platformOperationsSourceV1Schema = z.enum([
  "queue",
  "job",
  "outbox",
  "reconciliation",
  "backup",
  "certificate",
  "provider",
  "capacity",
  "slo",
])

export const platformOperationsSeverityV1Schema = z.enum([
  "info",
  "warning",
  "critical",
])

export const platformOperationsStateV1Schema = z.enum([
  "open",
  "acknowledged",
  "retrying",
  "waiting",
  "degraded",
  "uncertain",
  "resolved",
])

export const platformOperationsRetryAttemptV1Schema = z.object({
  attemptNumber: z.number().int().positive().max(1_000_000),
  outcome: z.enum(["succeeded", "failed", "uncertain"]),
  stableCode: codeSchema.nullable(),
  attemptedAt: timestampSchema,
}).strict().superRefine((attempt, context) => {
  if (
    (attempt.outcome === "succeeded" && attempt.stableCode !== null)
    || (attempt.outcome !== "succeeded" && attempt.stableCode === null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["stableCode"],
      message: "Retry outcome and stable code must agree.",
    })
  }
})

export const platformOperationsQueueItemV1Schema = z.object({
  contractVersion: z.literal(1),
  queueItemId: z.uuid(),
  targetTenantId: tenantIdSchema.nullable(),
  environment: platformProviderEnvironmentV1Schema,
  source: platformOperationsSourceV1Schema,
  severity: platformOperationsSeverityV1Schema,
  state: platformOperationsStateV1Schema,
  owner: codeSchema,
  slaDueAt: timestampSchema,
  correlationId: z.uuid(),
  diagnosticCode: codeSchema,
  recommendedAction: codeSchema,
  retryHistory: z.array(platformOperationsRetryAttemptV1Schema).max(10),
  runbookRef: z.string().regex(/^runbook:\/\/[a-z0-9][a-z0-9/_.-]{2,191}$/),
  observedAt: timestampSchema,
  version: z.number().int().positive(),
  valueFree: z.literal(true),
}).strict().superRefine((item, context) => {
  for (let index = 0; index < item.retryHistory.length; index += 1) {
    if (item.retryHistory[index]?.attemptNumber !== index + 1) {
      context.addIssue({
        code: "custom",
        path: ["retryHistory", index, "attemptNumber"],
        message: "Retry history must be bounded and contiguous.",
      })
    }
  }
})

export const platformOperationsWorkspaceFiltersV1Schema = z.object({
  environment: platformProviderEnvironmentV1Schema.nullable(),
  exactTenantId: tenantIdSchema.nullable(),
  source: platformOperationsSourceV1Schema.nullable(),
  severity: platformOperationsSeverityV1Schema.nullable(),
  state: platformOperationsStateV1Schema.nullable(),
  owner: codeSchema.nullable(),
  ownedOnly: z.boolean(),
  includeResolved: z.boolean(),
}).strict()

export const platformOperationsWorkspaceQueryV1Schema = z.object({
  envelope: platformQueryEnvelopeV1Schema.extend({
    queryType: z.literal("PlatformOperationsWorkspace"),
  }),
  filters: platformOperationsWorkspaceFiltersV1Schema,
  asOf: timestampSchema,
  page: z.object({
    limit: z.number().int().min(1).max(100),
    cursor: z.uuid().nullable(),
  }).strict(),
}).strict()

export const platformOperationsWorkspaceSummaryV1Schema = z.object({
  visibleCount: z.number().int().min(0).max(100),
  criticalCount: z.number().int().min(0).max(100),
  overdueCount: z.number().int().min(0).max(100),
  uncertainCount: z.number().int().min(0).max(100),
  retryingCount: z.number().int().min(0).max(100),
}).strict()

export const platformOperationsWorkspacePageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: timestampSchema,
  partial: z.boolean(),
  summary: platformOperationsWorkspaceSummaryV1Schema,
  items: z.array(platformOperationsQueueItemV1Schema).max(100),
  nextCursor: z.uuid().nullable(),
  valueFree: z.literal(true),
}).strict()

export type PlatformOperationsSourceV1 = z.infer<
  typeof platformOperationsSourceV1Schema
>
export type PlatformOperationsSeverityV1 = z.infer<
  typeof platformOperationsSeverityV1Schema
>
export type PlatformOperationsStateV1 = z.infer<
  typeof platformOperationsStateV1Schema
>
export type PlatformOperationsQueueItemV1 = z.infer<
  typeof platformOperationsQueueItemV1Schema
>
export type PlatformOperationsWorkspaceFiltersV1 = z.infer<
  typeof platformOperationsWorkspaceFiltersV1Schema
>
export type PlatformOperationsWorkspacePageV1 = z.infer<
  typeof platformOperationsWorkspacePageV1Schema
>
